"use client"

import React, {useState, useEffect, useRef} from "react"
import {Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis, Tooltip, ResponsiveContainer} from "recharts"
import {ChartContainer} from "@/components/ui/chart"
import {CircleHelp, Download, ChevronDown} from "lucide-react"
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"
import {cn} from "@/lib/utils"
import axiosInstance from "@/api/axios"
import {Button} from "@/components/ui/button"
import {useTranslation} from 'react-i18next'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getColorScheme } from "@/lib/colorSchemes";
import { useLivePolling } from "@/hooks/useLivePolling"

interface StatsProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string
    title?: string
    timeRange?: string
    bins?: string[]
    customDateRange?: [{
        startDate: Date | null;
        endDate: Date | null;
        key: string;
    }];
    isCustomDateRange?: boolean;
}

interface WasteData {
    period: string
    organic: number
    pmd: number
    paper: number
    residual: number
}

export function Stats({
    className,
    title,
    timeRange,
    bins = [],
    customDateRange,
    isCustomDateRange,
    ...props
}: StatsProps) {
    const { t } = useTranslation();
    const [wasteData, setWasteData] = React.useState<WasteData[]>([])
    const [selfAdjust, setSelfAdjust] = React.useState<boolean>(false)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<string | null>(null)
    const [colors, setColors] = React.useState(() => {
        const scheme = getColorScheme([], {});
        return { organic: scheme.Organic, pmd: scheme.PMD, paper: scheme.Paper, residual: scheme.Residual };
    })
    const exportRef = useRef<HTMLDivElement>(null);

    // ── Resolve color scheme based on the country of the selected bins ────────
    useEffect(() => {
        if (!bins || bins.length === 0) {
            const scheme = getColorScheme([], {});
            setColors({ organic: scheme.Organic, pmd: scheme.PMD, paper: scheme.Paper, residual: scheme.Residual });
            return
        }
        axiosInstance.get('/get_bins')
            .then(res => {
                const allBins: { unique_identifier: string; country?: string }[] = res.data
                const binTypeMap: Record<string, { country?: string }> = {}
                allBins.forEach(b => {
                    if (bins.includes(b.unique_identifier)) {
                        binTypeMap[b.unique_identifier] = { country: b.country }
                    }
                })
                const scheme = getColorScheme(bins, binTypeMap)
                setColors({ organic: scheme.Organic, pmd: scheme.PMD, paper: scheme.Paper, residual: scheme.Residual })
            })
            .catch(() => {
                const scheme = getColorScheme([], {});
                setColors({ organic: scheme.Organic, pmd: scheme.PMD, paper: scheme.Paper, residual: scheme.Residual });
            })
    }, [bins])

    const chartConfig = {
        organic:  { label: t("organiclabel"),  color: colors.organic  },
        pmd:      { label: t("pmdlabel"),       color: colors.pmd      },
        paper:    { label: t("paperlabel"),     color: colors.paper    },
        residual: { label: t("residuallabel"), color: colors.residual },
    }

    // --- HELPER: Format Date to YYYY-MM-DD using Local Time ---
    const formatDateLocal = (date: Date | string) => {
        const d = new Date(date);
        if (isNaN(d.getTime())) return "";
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const generateProcessedCSVString = (data: WasteData[]) => {
        if (!data || data.length === 0) return t("noDataAvailable");
        const unit = selfAdjust ? 'kg' : 'g';
        const sectionHeader = [
            `"${t("reportTitle", "WASTE MANAGEMENT REPORT")}"`,
            `"${t("generatedDate")}: ${new Date().toLocaleString()}"`,
            "",
            `"--- ${t("summarySection", "SECTION 1: SUMMARY TABLES")} ---"`,
            `"${t("summaryExpl", "This table shows the total combined weight for each time period, matching the chart.")}"`,
            ""
        ].join("\n");
        const headers = [
            t("periodLabel", "Time Period"),
            `${t("organiclabel")} (${unit})`,
            `${t("pmdlabel")} (${unit})`,
            `${t("paperlabel")} (${unit})`,
            `${t("residuallabel")} (${unit})`
        ];
        const rows = data.map(item => [
            item.period,
            item.organic.toFixed(3),
            item.pmd.toFixed(3),
            item.paper.toFixed(3),
            item.residual.toFixed(3)
        ].join(","));
        return `${sectionHeader}\n${headers.join(",")}\n${rows.join("\n")}`;
    };

    const handleCombinedExport = async () => {
        if (!timeRange && !isCustomDateRange) return;
        setLoading(true);
        try {
            const processedPart = generateProcessedCSVString(wasteData);
            let rawPart = "";
            try {
                const params: any = { bins: bins.join(",") };
                if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
                    params.startDate = formatDateLocal(customDateRange[0].startDate);
                    params.endDate = formatDateLocal(customDateRange[0].endDate);
                } else {
                    params.timeRange = timeRange;
                }
                const response = await axiosInstance.get("/stats_v2/raw-data", {
                    params,
                    paramsSerializer: { indexes: null },
                });
                if (response.data && Array.isArray(response.data)) {
                    const sectionHeader = [
                        "", "",
                        `"--------------------------------------------------"`,
                        `"--- ${t("detailsSection", "SECTION 2: DETAILED RECORD LIST")} ---"`,
                        `"${t("detailsExpl", "Below is a list of every single time a bin was used or emptied during this period.")}"`,
                        ""
                    ].join("\n");

                    const headers = [
                        t("colBinName", "Bin Name"),
                        t("colTime", "Date & Time Collected"),
                        t("colCategory", "Specific Category"),
                        t("colType", "Main Waste Type"),
                        t("colWeight", "Weight"),
                        t("colExpectedStream", "Expected Stream"),
                        t("colContamination", "Contamination Y/N"),
                    ];

                    const csvRows = response.data.map((item: any) => {
                        // Convert UTC timestamp to Amsterdam time (CET/CEST)
                        let displayTime = '-'
                        if (item.time_collected && item.time_collected !== '-') {
                            try {
                                const dt = new Date(item.time_collected)
                                displayTime = dt.toLocaleString('nl-NL', {
                                    timeZone: 'Europe/Amsterdam',
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false,
                                })
                            } catch {
                                displayTime = item.time_collected
                            }
                        }
                        return [
                            item.bin || '-',
                            `"${displayTime}"`,
                            item.sub_class_waste_type || '-',
                            item.waste_type || '-',
                            item.waste_weight || 0,
                            item.expected_stream || '-',
                            item.contamination || '-',
                        ].join(",")
                    });

                    rawPart = `${sectionHeader}\n${headers.join(",")}\n${csvRows.join("\n")}`;
                }
            } catch (rawError) {
                rawPart = `\n\n"${t("errorFetchingDetails", "Could not retrieve detailed records at this time.")}"`;
            }

            const finalCSVContent = processedPart + rawPart;
            const blob = new Blob([finalCSVContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const rangeLabel = isCustomDateRange ? 'custom' : timeRange;
            link.setAttribute("download", `waste_report_${rangeLabel}_${dateStr}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setLoading(false);
        }
    };

    // ── Extracted fetch: reused by the initial/filter-change effect below
    // AND by the live-polling hook. Background polls do NOT toggle the
    // loading spinner — only the initial/filter-triggered fetch does.
    const fetchStats = React.useCallback((isBackgroundRefresh = false) => {
        if (!timeRange && !isCustomDateRange) return;
        if (!isBackgroundRefresh) {
            setLoading(true)
            setError(null)
        }
        const params: any = { bins: bins.join(",") };
        if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
            params.startDate = formatDateLocal(customDateRange[0].startDate);
            params.endDate = formatDateLocal(customDateRange[0].endDate);
        } else {
            params.timeRange = timeRange;
        }
        axiosInstance
            .get("/stats_v2", { params, paramsSerializer: { indexes: null } })
            .then((response) => {
                if (response.data && Array.isArray(response.data)) {
                    const lastElement = response.data[response.data.length - 1];
                    const shouldAdjust = lastElement?.self_adjust || false;
                    setSelfAdjust(shouldAdjust);
                    const dataWithoutFlag = response.data.slice(0, -1);
                    const processedData = dataWithoutFlag.map((item: any) => ({
                        period: item.period,
                        organic: Number(item.organic) || 0,
                        pmd: Number(item.pmd) || 0,
                        paper: Number(item.paper) || 0,
                        residual: Number(item.residual) || 0
                    }));
                    setWasteData(processedData);
                } else if (!isBackgroundRefresh) {
                    setError(t("invalidDataFormat"))
                    setWasteData([])
                }
            })
            .catch(() => { if (!isBackgroundRefresh) setError(t("failedToFetchWasteStats")) })
            .finally(() => { if (!isBackgroundRefresh) setLoading(false) })
    }, [timeRange, bins, customDateRange, isCustomDateRange, t])

    // Initial fetch + refetch on filter change (shows loading state)
    React.useEffect(() => {
        fetchStats(false)
    }, [fetchStats])

    // Background live refresh every ~30s — paused while tab is hidden,
    // resumes with an immediate refresh when tab regains focus.
    // Disabled when there's nothing to poll for (no bins selected).
    useLivePolling(
        () => fetchStats(true),
        30000,
        [fetchStats],
        bins.length > 0 && (!!timeRange || !!isCustomDateRange)
    )

    const handleExportPNG = async () => {
        if (!exportRef.current) return;
        const elementsToHide = [
            ...exportRef.current.querySelectorAll('.no-export, .waste-generation-export')
        ];
        elementsToHide.forEach(el => { (el as HTMLElement).style.display = 'none'; });
        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(exportRef.current, {
                scale: 2, logging: false, useCORS: true, backgroundColor: '#FFFFFF'
            });
            const link = document.createElement('a');
            const rangeLabel = isCustomDateRange ? 'custom' : (timeRange || 'data');
            link.download = `waste_stats_${rangeLabel}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            elementsToHide.forEach(el => { (el as HTMLElement).style.display = ''; });
        }
    };

    const formatValue = (value: number) => {
        const absValue = Math.abs(value);
        if (absValue < 1) return absValue.toFixed(3);
        else if (absValue < 10) return absValue.toFixed(2);
        else return absValue.toFixed(1);
    };

    return (
        <div
            ref={exportRef}
            className={cn("lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section", className)}
            data-export-data={JSON.stringify({
                title: title || t("wasteProductionByTimePeriod"),
                headers: [t("periodLabel"), `${t("organiclabel")} (${selfAdjust ? 'kg' : 'g'})`, `${t("pmdlabel")} (${selfAdjust ? 'kg' : 'g'})`, `${t("paperlabel")} (${selfAdjust ? 'kg' : 'g'})`, `${t("residuallabel")} (${selfAdjust ? 'kg' : 'g'})`],
                rows: wasteData.map(item => [
                    item.period,
                    item.organic.toFixed(3),
                    item.pmd.toFixed(3),
                    item.paper.toFixed(3),
                    item.residual.toFixed(3)
                ]),
                chartData: wasteData.map(item => ({
                    x: item.period,
                    y: item.organic + item.pmd + item.paper + item.residual
                }))
            })}
            {...props}
        >
            <div className="relative">
                <div className="flex justify-between items-center pb-2">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{title || t("wasteProductionByTimePeriod")}</h4>
                        {isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {(() => {
                                    const startDate = customDateRange[0].startDate instanceof Date
                                        ? customDateRange[0].startDate
                                        : new Date(customDateRange[0].startDate);
                                    const endDate = customDateRange[0].endDate instanceof Date
                                        ? customDateRange[0].endDate
                                        : new Date(customDateRange[0].endDate);
                                    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                                        return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
                                    }
                                    return '';
                                })()}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center space-x-2 waste-generation-export no-export">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={wasteData.length === 0 || loading}
                                >
                                    <Download className="h-4 w-4 mr-1" />
                                    {t("exportlabel")}
                                    <ChevronDown className="h-4 w-4 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleCombinedExport} disabled={wasteData.length === 0 || loading}>
                                    {t("downloadAsCSV")}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportPNG} disabled={wasteData.length === 0 || loading}>
                                    {t("downloadAsPNG")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <CircleHelp className="h-5 w-5 text-muted-foreground no-export" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t("wasteStatsTooltip")}</p>
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>
            <div className="px-2 pt-4 sm:px-6 sm:pt-6" style={{ width: '100%', height: '100%' }}>
                {loading ? (
                    <div className="h-full w-full flex flex-col items-center justify-center">
                        <div className="text-muted-foreground">{t("loadingWasteStats")}</div>
                    </div>
                ) : error ? (
                    <div className="h-full w-full flex flex-col items-center justify-center">
                        <div className="text-destructive">{error}</div>
                    </div>
                ) : (
                    <ChartContainer config={chartConfig}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={wasteData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                barCategoryGap={2}
                                barGap={0}
                            >
                                <CartesianGrid stroke="lightgrey" vertical={false} />
                                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                                <YAxis
                                    tickFormatter={(value) => `${formatValue(value)} ${selfAdjust ? 'kg' : 'g'}`}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                                    content={({ active, payload, label }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <div className="rounded-lg border bg-background p-3 shadow-lg">
                                                    <div className="font-medium mb-2">{label}</div>
                                                    {payload
                                                        .filter(entry => entry.value !== 0)
                                                        .sort((a, b) => Math.abs(Number(b.value)) - Math.abs(Number(a.value)))
                                                        .map((entry) => (
                                                            <div key={entry.name} className="flex items-center justify-between gap-3 text-sm">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                                                    <span>{entry.name}:</span>
                                                                </div>
                                                                <span className="font-medium">
                                                                    {formatValue(Number(entry.value))} {selfAdjust ? 'kg' : 'g'}
                                                                </span>
                                                            </div>
                                                        ))
                                                    }
                                                    {payload.every(entry => entry.value === 0) && (
                                                        <div className="text-sm text-muted-foreground">{t("noDataForPeriod")}</div>
                                                    )}
                                                </div>
                                            )
                                        }
                                        return null
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="organic"  name={t("organiclabel")}  fill={colors.organic}  minPointSize={1} />
                                <Bar dataKey="pmd"      name={t("pmdlabel")}      fill={colors.pmd}      minPointSize={1} />
                                <Bar dataKey="paper"    name={t("paperlabel")}    fill={colors.paper}    minPointSize={1} />
                                <Bar dataKey="residual" name={t("residuallabel")} fill={colors.residual} minPointSize={1} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                )}
            </div>
        </div>
    )
}